import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Chip,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { adminApiClient } from '../../adminApiClient';
import { Campaign, CampaignUser, CreateCampaignRequest, RoleType, CampaignStatus, CampaignChannel } from '../../models/Campaign';
import { format } from 'date-fns';
import { getSmsSegmentCount, getEstimatedSmsPrice } from '../../utils/smsUtils';
import { useThemeContext } from '../../contexts/ThemeContext';

const CampaignsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignUsers, setCampaignUsers] = useState<CampaignUser[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tempSelectedUserIds, setTempSelectedUserIds] = useState<string[]>([]);
  const [hasCustomizedUsers, setHasCustomizedUsers] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateCampaignRequest>({
    name: '',
    text: '',
    channel: CampaignChannel.SMS,
    countryCode: '',
    roles: [],
    includedUserIds: [],
  });

  // Fetch campaigns, users, and countries on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [campaignsData, usersData, countriesData] = await Promise.all([
          adminApiClient.getAllCampaigns(),
          adminApiClient.getCampaignUsers(),
          adminApiClient.getCampaignCountries(),
        ]);
        setCampaigns(campaignsData);
        setCampaignUsers(usersData);
        setCountries(countriesData);
      } catch (err) {
        setError(t('campaigns.fetchError', 'Failed to fetch campaigns data'));
        console.error('Error fetching campaigns data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  // SMS segment count and estimated price for create form
  const formSmsSegments = useMemo(() => getSmsSegmentCount(formData.text), [formData.text]);
  const formEstimatedPrice = useMemo(
    () => getEstimatedSmsPrice(formData.includedUserIds.length, formSmsSegments),
    [formData.includedUserIds.length, formSmsSegments]
  );

  // Calculate filtered users based on form selections
  const filteredUsers = useMemo(() => {
    if (!formData.countryCode || formData.roles.length === 0) {
      return [];
    }
    return campaignUsers.filter(user => {
      const countryMatch = user.countryCode === formData.countryCode;
      const roleMatch = formData.roles.includes(user.role);
      const channelMatch =
        formData.channel === CampaignChannel.MOBILE_PUSH ? user.mobileAppInstalled : true;
      return countryMatch && roleMatch && channelMatch;
    });
  }, [campaignUsers, formData.countryCode, formData.roles, formData.channel]);

  // Update includedUserIds when filters change (only if user hasn't customized)
  useEffect(() => {
    if (!customizeDialogOpen && !hasCustomizedUsers && filteredUsers.length > 0) {
      setFormData(prev => ({
        ...prev,
        includedUserIds: filteredUsers.map(user => user.id),
      }));
    }
  }, [filteredUsers, customizeDialogOpen, hasCustomizedUsers]);

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      countryCode: event.target.value,
    }));
    // Reset customization flag when filters change
    setHasCustomizedUsers(false);
  };

  const handleRolesChange = (event: SelectChangeEvent<RoleType[]>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      roles: typeof value === 'string' ? (value.split(',') as RoleType[]) : value,
    }));
    // Reset customization flag when filters change
    setHasCustomizedUsers(false);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      name: event.target.value,
    }));
  };

  const handleChannelChange = (event: SelectChangeEvent<CampaignChannel>) => {
    setFormData(prev => ({
      ...prev,
      channel: event.target.value as CampaignChannel,
    }));
    setHasCustomizedUsers(false);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      text: event.target.value,
    }));
  };

  const handleOpenCustomizeDialog = () => {
    // Initialize temp state with current selections
    setTempSelectedUserIds([...formData.includedUserIds]);
    setCustomizeDialogOpen(true);
  };

  const handleCloseCustomizeDialog = () => {
    setCustomizeDialogOpen(false);
    // Reset temp state when closing
    setTempSelectedUserIds([]);
  };

  const handleSaveCustomizeDialog = () => {
    // Apply temp selections to form data
    setFormData(prev => ({
      ...prev,
      includedUserIds: [...tempSelectedUserIds],
    }));
    setHasCustomizedUsers(true);
    setCustomizeDialogOpen(false);
    setTempSelectedUserIds([]);
  };

  const handleCancelCustomizeDialog = () => {
    // Discard changes and close
    setCustomizeDialogOpen(false);
    setTempSelectedUserIds([]);
  };

  const handleToggleUser = (userId: string) => {
    setTempSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    setTempSelectedUserIds(filteredUsers.map(user => user.id));
  };

  const handleUnselectAll = () => {
    setTempSelectedUserIds([]);
  };

  const handleCreateCampaign = async () => {
    if (!formData.name || !formData.text || !formData.countryCode || formData.roles.length === 0 || !formData.channel) {
      setError(t('campaigns.validationError', 'Please fill in all required fields'));
      return;
    }

    if (formData.includedUserIds.length === 0) {
      setError(t('campaigns.noUsersError', 'Please select at least one user'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await adminApiClient.createCampaign(formData);
      // Reset form
      setFormData({
        name: '',
        text: '',
        channel: CampaignChannel.SMS,
        countryCode: '',
        roles: [],
        includedUserIds: [],
      });
      setHasCustomizedUsers(false);
      // Refresh campaigns list
      const campaignsData = await adminApiClient.getAllCampaigns();
      setCampaigns(campaignsData);
    } catch (err) {
      setError(t('campaigns.createError', 'Failed to create campaign'));
      console.error('Error creating campaign:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerSend = async (campaignId: string) => {
    try {
      setActionLoading(campaignId);
      await adminApiClient.triggerSend(campaignId);
      // Refresh campaigns list
      const campaignsData = await adminApiClient.getAllCampaigns();
      setCampaigns(campaignsData);
    } catch (err) {
      setError(t('campaigns.triggerSendError', 'Failed to trigger send'));
      console.error('Error triggering send:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryFailed = async (campaignId: string) => {
    try {
      setActionLoading(campaignId);
      await adminApiClient.triggerRetryFailed(campaignId);
      // Refresh campaigns list
      const campaignsData = await adminApiClient.getAllCampaigns();
      setCampaigns(campaignsData);
    } catch (err) {
      setError(t('campaigns.retryFailedError', 'Failed to retry failed messages'));
      console.error('Error retrying failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('campaigns.name', 'Name'),
      flex: 1.5,
    },
    {
      field: 'channel',
      headerName: t('campaigns.channel', 'Channel'),
      flex: 1,
      renderCell: (params: any) => (
        <Chip label={params.row.channel || CampaignChannel.SMS} size="small" variant="outlined" />
      ),
    },
    {
      field: 'countryCode',
      headerName: t('campaigns.countryCode', 'Country'),
      flex: 1,
    },
    {
      field: 'roles',
      headerName: t('campaigns.roles', 'Roles'),
      flex: 1.5,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {params.row.roles?.map((role: RoleType) => (
            <Chip key={role} label={role} size="small" />
          ))}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: t('campaigns.status', 'Status'),
      flex: 1,
      renderCell: (params: any) => (
        <Chip
          label={params.row.status}
          color={
            params.row.status === CampaignStatus.SENT
              ? 'success'
              : params.row.status === CampaignStatus.FAILED
              ? 'error'
              : params.row.status === CampaignStatus.SENT_PARTIALLY
              ? 'warning'
              : 'default'
          }
          size="small"
        />
      ),
    },
    {
      field: 'includedUserIds',
      headerName: t('campaigns.users', 'Users'),
      flex: 1,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {params.row.includedUserIds?.length || 0}
        </Typography>
      ),
    },
    {
      field: 'smsSegments',
      headerName: t('campaigns.smsSegments', 'SMS Segments'),
      flex: 0.8,
      renderCell: (params: any) => {
        const channel = params.row.channel || CampaignChannel.SMS;
        if (channel === CampaignChannel.MOBILE_PUSH) {
          return <Typography variant="body2">-</Typography>;
        }
        const segments = getSmsSegmentCount(params.row.text || '');
        return <Typography variant="body2">{segments}</Typography>;
      },
    },
    {
      field: 'estimatedPrice',
      headerName: t('campaigns.estimatedPrice', 'Est. price'),
      flex: 1,
      renderCell: (params: any) => {
        const channel = params.row.channel || CampaignChannel.SMS;
        if (channel === CampaignChannel.MOBILE_PUSH) {
          return <Typography variant="body2">$0.0000</Typography>;
        }
        const segments = getSmsSegmentCount(params.row.text || '');
        const users = params.row.includedUserIds?.length || 0;
        const price = getEstimatedSmsPrice(users, segments);
        return <Typography variant="body2">${price.toFixed(4)}</Typography>;
      },
    },
    {
      field: 'failedPhoneNumbers',
      headerName: t('campaigns.failed', 'Failed'),
      flex: 1,
      renderCell: (params: any) => (
        <Typography variant="body2" color={params.row.failedPhoneNumbers?.length > 0 ? 'error' : 'text.secondary'}>
          {params.row.failedPhoneNumbers?.length || 0}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: t('campaigns.createdAt', 'Created At'),
      flex: 1.5,
      renderCell: (params: any) => {
        const date = params.row?.createdAt;
        if (!date) return '';
        try {
          return format(new Date(date), 'MMM d, yyyy HH:mm');
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 200,
      sortable: false,
      renderCell: (params: any) => {
        const campaign = params.row as Campaign;
        const isLoading = actionLoading === campaign.id;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {campaign.status === CampaignStatus.DRAFT && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleTriggerSend(campaign.id)}
                disabled={isLoading}
              >
                {t('campaigns.triggerSend', 'Trigger Send')}
              </Button>
            )}
            {campaign.status === CampaignStatus.SENT_PARTIALLY && (
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={() => handleRetryFailed(campaign.id)}
                disabled={isLoading}
              >
                {t('campaigns.retryFailed', 'Retry Failed')}
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  if (loading && campaigns.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1600, width: '100%', mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Create Campaign Form - 50% width */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('campaigns.createTitle', 'Create Campaign')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Channel */}
              <FormControl fullWidth required>
                <InputLabel id="channel-label">{t('campaigns.channel', 'Channel')}</InputLabel>
                <Select
                  labelId="channel-label"
                  value={formData.channel}
                  onChange={handleChannelChange}
                  label={t('campaigns.channel', 'Channel')}
                  required
                >
                  <MenuItem value={CampaignChannel.SMS}>{CampaignChannel.SMS}</MenuItem>
                  <MenuItem value={CampaignChannel.MOBILE_PUSH}>{CampaignChannel.MOBILE_PUSH}</MenuItem>
                </Select>
              </FormControl>

              {/* Country Code */}
              <FormControl fullWidth required>
                <InputLabel id="country-code-label">{t('campaigns.countryCode', 'Country Code')}</InputLabel>
                <Select
                  labelId="country-code-label"
                  value={formData.countryCode}
                  onChange={handleCountryChange}
                  label={t('campaigns.countryCode', 'Country Code')}
                  required
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 300 }
                    }
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Roles */}
              <FormControl fullWidth required>
                <InputLabel id="roles-label">{t('campaigns.roles', 'Roles')}</InputLabel>
                <Select
                  labelId="roles-label"
                  multiple
                  value={formData.roles}
                  onChange={handleRolesChange}
                  label={t('campaigns.roles', 'Roles')}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5, minHeight: '20px', alignItems: 'center' }}>
                      {selected.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('campaigns.selectRoles', 'Select roles...')}
                        </Typography>
                      ) : (
                        selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))
                      )}
                    </Box>
                  )}
                  required
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 300 }
                    }
                  }}
                >
                  {Object.values(RoleType)
                    .map((role) => (
                      <MenuItem key={role} value={role}>
                        <Checkbox checked={formData.roles.indexOf(role) > -1} />
                        {role}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('campaigns.title', 'Title')}
                value={formData.name}
                onChange={handleNameChange}
                required
              />

              <TextField
                fullWidth
                label={t('campaigns.text', 'Text')}
                value={formData.text}
                onChange={handleTextChange}
                multiline
                rows={4}
                required
              />

              {/* SMS segments & estimated price */}
              {(formData.text.length > 0 || formData.includedUserIds.length > 0) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  {formData.channel === CampaignChannel.SMS && (
                    <Typography variant="body2" color="text.secondary">
                      {t('campaigns.smsSegments', 'SMS segments')}: <strong>{formSmsSegments}</strong>
                      {formData.text.length > 0 && ` (${formData.text.length} ${t('campaigns.chars', 'chars')})`}
                    </Typography>
                  )}
                  {formData.channel === CampaignChannel.SMS && formData.includedUserIds.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {t('campaigns.estimatedPrice', 'Est. price')}: <strong>${formEstimatedPrice.toFixed(4)}</strong>
                    </Typography>
                  )}
                </Box>
              )}

              {/* Right-justified Selected Users + Customize */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1">
                  {t('campaigns.selectedUsers', 'Selected Users')}: <strong>{formData.includedUserIds.length}</strong>
                </Typography>
                <Button variant="outlined" onClick={handleOpenCustomizeDialog} disabled={!formData.countryCode || formData.roles.length === 0}>
                  {t('campaigns.customize', 'Customize')}
                </Button>
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateCampaign}
                disabled={loading}
                fullWidth
                size="large"
              >
                {t('campaigns.create', 'Create Campaign')}
              </Button>
            </Stack>
          </Card>
        </Grid>

        {/* Campaigns Table - 100% width on new row */}
        <Grid item xs={12}>
          <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('campaigns.title', 'Campaigns')}
            </Typography>
            <Divider sx={{ mb: { xs: 2, md: 3 } }} />

            {isMobile ? (
              <Box sx={{ mt: 2 }}>
                {campaigns.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {t('campaigns.noCampaigns', 'No campaigns found')}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {campaigns.map((campaign) => {
                      const isLoading = actionLoading === campaign.id;
                      return (
                        <Card key={campaign.id} variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            {campaign.name}
                          </Typography>
                          <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('campaigns.channel', 'Channel')}: {campaign.channel || CampaignChannel.SMS}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('campaigns.countryCode', 'Country')}: {campaign.countryCode}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {campaign.roles?.map((role) => (
                                <Chip key={role} label={role} size="small" />
                              ))}
                            </Box>
                            <Chip
                              label={campaign.status}
                              color={
                                campaign.status === CampaignStatus.SENT
                                  ? 'success'
                                  : campaign.status === CampaignStatus.FAILED
                                  ? 'error'
                                  : campaign.status === CampaignStatus.SENT_PARTIALLY
                                  ? 'warning'
                                  : 'default'
                              }
                              size="small"
                              sx={{ width: 'fit-content' }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {t('campaigns.users', 'Users')}: {campaign.includedUserIds?.length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('campaigns.smsSegments', 'SMS Segments')}:{' '}
                              {(campaign.channel || CampaignChannel.SMS) === CampaignChannel.MOBILE_PUSH
                                ? '-'
                                : getSmsSegmentCount(campaign.text || '')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('campaigns.estimatedPrice', 'Est. price')}: $
                              {(campaign.channel || CampaignChannel.SMS) === CampaignChannel.MOBILE_PUSH
                                ? '0.0000'
                                : getEstimatedSmsPrice(
                                    campaign.includedUserIds?.length || 0,
                                    getSmsSegmentCount(campaign.text || '')
                                  ).toFixed(4)}
                            </Typography>
                            {campaign.failedPhoneNumbers && campaign.failedPhoneNumbers.length > 0 && (
                              <Typography variant="body2" color="error">
                                {t('campaigns.failed', 'Failed')}: {campaign.failedPhoneNumbers.length}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {t('campaigns.createdAt', 'Created At')}:{' '}
                              {campaign.createdAt ? format(new Date(campaign.createdAt), 'MMM d, yyyy HH:mm') : ''}
                            </Typography>
                          </Stack>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {campaign.status === CampaignStatus.DRAFT && (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleTriggerSend(campaign.id)}
                                disabled={isLoading}
                                fullWidth
                              >
                                {t('campaigns.triggerSend', 'Trigger Send')}
                              </Button>
                            )}
                            {campaign.status === CampaignStatus.SENT_PARTIALLY && (
                              <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                onClick={() => handleRetryFailed(campaign.id)}
                                disabled={isLoading}
                                fullWidth
                              >
                                {t('campaigns.retryFailed', 'Retry Failed')}
                              </Button>
                            )}
                          </Box>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            ) : (
              <DataGrid
                rows={campaigns}
                columns={columns}
                autoHeight
                getRowId={(row) => row.id}
                sx={{
                  width: '100%',
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-root': {
                    width: '100%',
                  },
                }}
              />
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Customize Users Dialog */}
      <Dialog
        open={customizeDialogOpen}
        onClose={handleCloseCustomizeDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 3 },
            margin: { xs: 0, sm: 2 },
            maxWidth: { xs: '100%', sm: '800px' },
            maxHeight: { xs: '100vh', sm: '90vh' },
            boxShadow: mode === 'dark'
              ? '0px 8px 32px rgba(0, 0, 0, 0.6)'
              : '0px 4px 20px rgba(0, 0, 0, 0.12)',
            background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
            border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('campaigns.customizeUsers', 'Customize Users')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleSelectAll}>
                {t('campaigns.selectAll', 'Select All')}
              </Button>
              <Button size="small" onClick={handleUnselectAll}>
                {t('campaigns.unselectAll', 'Unselect All')}
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <Stack spacing={1}>
              {filteredUsers.map((user) => (
                <FormControlLabel
                  key={user.id}
                  control={
                    <Checkbox
                      checked={tempSelectedUserIds.includes(user.id)}
                      onChange={() => handleToggleUser(user.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{user.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.phoneNumber} • {user.countryCode} • {user.role}
                      </Typography>
                      <Typography variant="caption" color={user.mobileAppInstalled ? 'success.main' : 'text.secondary'}>
                        {t('campaigns.mobileAppInstalled', 'Mobile app installed')}: {user.mobileAppInstalled ? t('common.yes', 'Yes') : t('common.no', 'No')}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Stack>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleCancelCustomizeDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveCustomizeDialog}>
            {t('campaigns.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignsPage;
